import React, { useState, useRef, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { InputLabel } from '@wajeez-web/components/ui';
import { TextFormType } from '../Types';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { IoMdEyeOff, IoMdEye } from 'react-icons/io';

export interface FormTextInputProps<TFormFields extends FieldValues>
  extends UseControllerProps<TFormFields> {
  fieldType: TextFormType;
  isRtl?: boolean;
  id?: string;
  shape?: 'circle' | 'squre';
  color?: string;
  bg?: 'primary' | 'secondary' | string;
  disabled?: boolean;
  size?: 'sm' | 'med' | 'lg';
  prefix?: React.ReactElement;
  suffix?: JSX.Element | null;
  focusClass?: string;
  borderClass?: string;
  inputtype?: 'text' | 'password' | 'email';
  showErrors?: boolean;
  errorMessages?: {
    required?: string;
    pattern?: string;
    minLength?: string;
  };
  autoComplete?: string;
  readOnly?: boolean;
  heightClassName?: string;
  widthClassName?: string;
  // New prop for keywords functionality
  enableKeywords?: boolean;
}

const FormDynamicTextInput = <TFormFields extends FieldValues>({
  fieldType,
  id,
  isRtl,
  shape = 'squre',
  color = 'textPrimary',
  bg = 'white',
  prefix,
  suffix,
  size = 'med',
  disabled,
  focusClass,
  borderClass = 'border-gray-300',
  inputtype = 'text',
  showErrors = true,
  errorMessages,
  autoComplete = 'off',
  heightClassName,
  widthClassName,
  enableKeywords = true,
  ...props
}: FormTextInputProps<TFormFields>) => {
  const [controlledInputType, setControlledInputType] = useState(inputtype);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const theme = useCommonSelector(({ common }) => common.settings.theme);

  const {
    field: { value, onChange, ...otherFields },
    fieldState,
  } = useController({
    ...props,
    rules: {
      required: fieldType.required ? 'Required' : undefined,
      pattern: fieldType.fieldOptions.pattern,
      minLength:
        fieldType.fieldOptions.minLength != undefined
          ? fieldType.fieldOptions.minLength
          : undefined,
      maxLength:
        fieldType.fieldOptions.maxLength != undefined
          ? fieldType.fieldOptions.maxLength
          : undefined,
      ...props.rules,
    },
  });

  const { t } = useTranslation();

  const isError = fieldState.error;

  switch (fieldState.error?.type) {
    case 'required':
      fieldState.error.message = errorMessages?.required;
      break;
    case 'pattern':
      fieldState.error.message = errorMessages?.pattern;
      break;
    case 'minLength':
      fieldState.error.message = errorMessages?.minLength;
      break;
    default:
      break;
  }

  // Parse current value to keywords array
  const keywords: string[] =
    enableKeywords && value ? value.split(',').filter(Boolean) : [];

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!enableKeywords) return;

    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeyword();
    } else if (
      e.key === 'Backspace' &&
      inputValue === '' &&
      keywords.length > 0
    ) {
      // Remove last keyword when backspace is pressed on empty input
      removeKeyword(keywords.length - 1);
    }
  };

  const addKeyword = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue) {
      const currentKeywords = keywords;
      const newKeywords = [...currentKeywords, trimmedValue];
      const newValue = newKeywords.join(',');

      onChange(newValue);
      setInputValue('');

      // Focus back to input after a brief delay
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const removeKeyword = (index: number) => {
    const newKeywords = keywords.filter((_, i) => i !== index);
    const newValue = newKeywords.join(',');
    onChange(newValue);

    // Focus back to input
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (enableKeywords) {
      setInputValue(e.target.value);
    } else {
      onChange(e.target.value);
    }
  };

  const getDisplayValue = () => {
    if (enableKeywords) {
      return inputValue;
    }
    return value || '';
  };

  return (
    <div className={`${widthClassName}`}>
      {fieldType.label && (
        <InputLabel
          text={
            t(
              `inputs.labels.${fieldType.label}`,
              fieldType.label.toString() || ''
            ) as string
          }
          name={otherFields.name}
          required={fieldType.required}
          hint={fieldType.span}
        />
      )}

      {/* Keywords display container */}
      {enableKeywords && keywords.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {keywords.map((keyword, index) => (
            <div
              key={index}
              className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm"
            >
              <span>{keyword}</span>
              <button
                type="button"
                onClick={() => removeKeyword(index)}
                className="ms-2 text-primary hover:text-primary-dark focus:outline-none"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        className={`relative ${
          shape === 'circle' ? 'rounded-full' : 'rounded-md'
        } shadow-sm`}
      >
        {prefix && (
          <div
            className={`absolute inset-y-0 ltr:left-0 rtl:right-0  ps-3 flex items-center pointer-events-none`}
          >
            <span className="text-gray-500 sm:text-sm">{prefix}</span>
          </div>
        )}
        <input
          id={id}
          dir={isRtl ? 'rtl' : 'ltr'}
          maxLength={fieldType.fieldOptions.maxLength}
          type={controlledInputType}
          disabled={disabled}
          autoComplete={autoComplete}
          required={fieldType.required}
          value={getDisplayValue()}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          {...otherFields}
          ref={inputRef}
          className={`${heightClassName} input ${size === 'med' && 'py-3'} ${
            size === 'lg' && 'py-5'
          } cursor-pointer ${
            shape === 'circle' ? 'rounded-full' : 'rounded-md'
          } bg-${bg} dark:!bg-dark-medium ${
            isError
              ? `text-red-500`
              : `text-${color} dark:placeholder:text-dark-light`
          } ${
            focusClass ||
            'focus:border-primary focus:ring-opacity-0 focus:ring-primary focus:ring-1 focus:ring-offset-0'
          } block w-full ${prefix && 'rtl:pr-10 ltr:pl-10'} sm:text-sm ${
            isError
              ? 'border-red-400 focus:ring-red-500 focus:border-red-400 '
              : borderClass || 'border-gray-300'
          }`}
          placeholder={`${t(
            `inputs.placeholders.${fieldType.placeHolder}`,
            fieldType.placeHolder || ''
          )}${!fieldType.label && fieldType.required ? '*' : ''}`}
        />
        {inputtype === 'password' && value && (
          <div
            className={`absolute inset-y-0 rtl:left-[25px] ltr:right-[25px] flex items-center cursor-pointer`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            onClick={() => {
              if (controlledInputType === 'password') {
                setControlledInputType('text');
              } else {
                setControlledInputType('password');
              }
            }}
          >
            {controlledInputType === 'password' ? (
              <IoMdEyeOff
                className={`${
                  theme === 'dark' ? 'text-white' : 'text-black'
                } text-[16px]`}
              />
            ) : (
              <IoMdEye
                className={`${
                  theme === 'dark' ? 'text-white' : 'text-black'
                } text-[16px]`}
              />
            )}
          </div>
        )}
        {suffix ? (
          <div
            className={`absolute inset-y-0 ltr:right-1 rtl:left-1 flex items-center`}
          >
            {suffix}
          </div>
        ) : null}
      </div>
      {isError && showErrors && (
        <div className="mt-2">
          <p className="mb-0 text-sm text-red-500">{`${t(
            `inputs.messages.${fieldState.error?.message}`,
            fieldState.error?.message || ''
          )}`}</p>
        </div>
      )}
    </div>
  );
};

export default FormDynamicTextInput;
